import type { Disposable } from "@/shared/contracts/lifecycle";

export interface AbsenceResumeTrigger {
  readonly hiddenAtMs: number;
  readonly resumedAtMs: number;
  readonly processedElapsedMs: number;
  readonly unprocessedElapsedMs: number;
}

export interface AbsenceLifecycleCoordinator extends Disposable {
  readonly hasOpenInterval: boolean;
  handleHidden(hiddenAtMs: number): boolean;
  handleCheckpoint(suspendedAtMs: number): boolean;
  recordGameplayProcessed(elapsedMs: number): void;
  handleResume(resumedAtMs: number): boolean;
  getUnprocessedElapsedMs(observedAtMs: number): number;
}

export function resolveSamePageOfflineBaselineAt(options: {
  readonly previousBaselineAtMs: number;
  readonly savedAtMs: number;
  readonly unprocessedElapsedMs: number;
}): number {
  return Math.max(
    sanitizeTimestamp(options.previousBaselineAtMs),
    sanitizeTimestamp(options.savedAtMs) -
      sanitizeElapsed(options.unprocessedElapsedMs),
  );
}

export function createAbsenceLifecycleCoordinator(options: {
  readonly captureCheckpoint: () => void | Promise<void>;
  readonly resume: (trigger: AbsenceResumeTrigger) => void;
  readonly onError: (error: unknown) => void;
}): AbsenceLifecycleCoordinator {
  let pending: {
    readonly hiddenAtMs: number;
    processedElapsedMs: number;
  } | null = null;
  let isDisposed = false;

  const captureCheckpoint = (): void => {
    try {
      void Promise.resolve(options.captureCheckpoint()).catch(options.onError);
    } catch (error) {
      options.onError(error);
    }
  };

  const handleHidden = (hiddenAtMs: number): boolean => {
    if (isDisposed || pending !== null) {
      return false;
    }
    pending = {
      hiddenAtMs: sanitizeTimestamp(hiddenAtMs),
      processedElapsedMs: 0,
    };
    captureCheckpoint();
    return true;
  };

  return {
    get hasOpenInterval() {
      return pending !== null;
    },
    handleHidden,
    handleCheckpoint(suspendedAtMs) {
      if (isDisposed) {
        return false;
      }
      if (pending === null) {
        return handleHidden(suspendedAtMs);
      }
      captureCheckpoint();
      return false;
    },
    recordGameplayProcessed(elapsedMs) {
      if (pending === null) {
        return;
      }
      pending.processedElapsedMs += sanitizeElapsed(elapsedMs);
    },
    handleResume(resumedAtMs) {
      if (isDisposed || pending === null) {
        return false;
      }
      const captured = pending;
      const safeResumedAtMs = Math.max(
        captured.hiddenAtMs,
        sanitizeTimestamp(resumedAtMs),
      );
      const intervalElapsedMs = safeResumedAtMs - captured.hiddenAtMs;
      const processedElapsedMs = Math.min(
        intervalElapsedMs,
        captured.processedElapsedMs,
      );
      try {
        options.resume({
          hiddenAtMs: captured.hiddenAtMs,
          resumedAtMs: safeResumedAtMs,
          processedElapsedMs,
          unprocessedElapsedMs: intervalElapsedMs - processedElapsedMs,
        });
      } catch (error) {
        options.onError(error);
        return false;
      }
      pending = null;
      return true;
    },
    getUnprocessedElapsedMs(observedAtMs) {
      if (pending === null) {
        return 0;
      }
      const intervalElapsedMs =
        Math.max(pending.hiddenAtMs, sanitizeTimestamp(observedAtMs)) -
        pending.hiddenAtMs;
      return Math.max(0, intervalElapsedMs - pending.processedElapsedMs);
    },
    dispose() {
      isDisposed = true;
    },
  };
}

function sanitizeTimestamp(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function sanitizeElapsed(value: number): number {
  return Number.isFinite(value) && value > 0 ? value : 0;
}
